interface BrowserPlatform {
  name?: string;
  version?: string;
  osFamily?: string;
  osVersion?: string;
  userAgent: string;
}
const MINIMUM_SUPPPORTED_VERSION_OF_OS_X_SAFARI = 11.0;
const MINIMUM_SUPPPORTED_VERSION_OF_IOS_SAFARI = 9.0;
const MINIMUM_SUPPPORTED_VERSION_OF_CHROME = 39.0;
const MINIMUM_SUPPPORTED_VERSION_OF_EDGE = 18.0;
const MINIMUM_SUPPPORTED_VERSION_OF_FIREFOX = 60.0;
const CHROME_MAJOR_VERSION_REGEXP_FOR_WEBVIEW = /Chrome\/(\d+)/;
// iOS Safari WebView User Agents do not contain 'safari' ref. https://stackoverflow.com/questions/4460205/detect-ipad-iphone-webview-via-javascript
// Due to some Custom UA adding Safari into their iOS WebView UAs, check for `Safari/X.X`
const SAFARI_WEBVIEW_DETECTION_REGEXP = /safari\//i;
// Webview Agent Pattern, check following references;
//   - https://github.com/atomantic/is-ua-webview/blob/master/data/rules.js
//   - https://developer.chrome.com/docs/multidevice/user-agent/#webview_user_agent
//   - https://www.chromium.org/updates/ua-reduction/
//   - https://blog.chromium.org/2021/05/update-on-user-agent-string-reduction.html
//   - https://stackoverflow.com/questions/71459572/not-possible-to-detect-webview-with-javascript-and-user-agent-on-old-android-ver
const WEBVIEW_REGEXP = /WebView/;
const SAFARI_WEBVIEW_DETECTION_REGEXP_EX = /(iPhone|iPod|iPad)(?!.*Safari)/i;
const ANDROID_WEBVIEW_DETECTION_REGEXP = /Android.*\bwv[);]|Android [45]\..*(wv|Chrome\/\d{2}\.0\.0\.0)/i;
const OLD_CHROME_ANDROID_WEBVIEW_DETECTION_REGEXP = /Linux; U; Android/i;
// Legacy Edge には "Edge/" が含まれるが、 Chromium Edge では "Edg/" となっている
// ref. https://github.com/bestiejs/platform.js/issues/178
const CHROMIUM_EDGE_DETECTION_REGEXP = /(Edg\/|EdgA\/|EdgiOS\/)/;

export const BrowserCompatibility = {
  // 各Platformの対応バージョン: Chrome/Firefoxは自動更新なので実装時点での最新バージョンから少し前くらいに設定、Safariについては `SendBeacon` が使えるバージョンに設定
  // iOS Safariではブラウザのバージョン = OSのバージョンとなっているようで、11.0からnavigator.sendBeaconが利用できるためサポート対象とする
  // FirefoxはバージョニングがPCとモバイルでずれているため、まずはPCのみ対応させる
  // `platformName` については右記を参照 https://github.com/bestiejs/platform.js/blob/master/doc/README.md#platformname
  isSupported({ name, version, osFamily, osVersion, userAgent }: BrowserPlatform) {
    if (name == null || version == null) return false;

    // Chrome のバージョンは一番古くまで遡ることになるであろう iOS 9 及び Android 5 をもとに決定
    // Android: https://en.wikipedia.org/wiki/Android_version_history
    // iOS: https://en.wikipedia.org/wiki/IOS_version_history
    // Chrome: https://en.wikipedia.org/wiki/Google_Chrome_version_history
    switch (name) {
      case 'Chrome':
      case 'Chrome Mobile':
        return this.evaluateChrome({ version });
      case 'Safari':
        return this.evaluateSafari({ version, osFamily, osVersion });
      case 'Firefox':
      case 'Firefox Mobile':
      case 'Firefox for iOS':
        return this.evaluateFirefox({ version });
      case 'Microsoft Edge':
        return this.evaluateEdge({ version });
      case 'Android Browser':
        return this.evaluateAndroidBrowser({ userAgent });
      default:
        return false;
    }
  },
  isWebView({ name, userAgent }: Pick<BrowserPlatform, 'name' | 'userAgent'>) {
    switch (name) {
      case 'Android Browser':
        return true;
      case 'Safari':
        return userAgent.match(SAFARI_WEBVIEW_DETECTION_REGEXP) === null;
      default:
        return (
          WEBVIEW_REGEXP.test(userAgent) ||
          SAFARI_WEBVIEW_DETECTION_REGEXP_EX.test(userAgent) ||
          ANDROID_WEBVIEW_DETECTION_REGEXP.test(userAgent) ||
          OLD_CHROME_ANDROID_WEBVIEW_DETECTION_REGEXP.test(userAgent)
        );
    }
  },
  isPushNotificationSupported({ name, userAgent }: Pick<BrowserPlatform, 'name' | 'userAgent'>): boolean {
    // Windows では v79 移行の Edge は Chromium ベースになっているが、
    // iOS などにポートされている Edge はプラットフォームの制約を受けて Chromium 以外の
    // レンダリングエンジンになっている点に注意。ここではプラットフォームがなんであるかに関わらず
    // Chromium ベースの Edge になっているかどうかだけを userAgent から判定している
    const isChromiumEdge =
      (name === 'Chrome' || name === 'Chrome Mobile') && CHROMIUM_EDGE_DETECTION_REGEXP.test(userAgent);

    return (
      'serviceWorker' in navigator &&
      // eslint-disable-next-line compat/compat
      'showNotification' in ServiceWorkerRegistration.prototype &&
      typeof PushSubscriptionOptions !== 'undefined' &&
      // eslint-disable-next-line no-prototype-builtins, compat/compat
      PushSubscriptionOptions.prototype.hasOwnProperty('applicationServerKey') &&
      (name === 'Chrome' || name === 'Chrome Mobile' || name === 'Safari') &&
      !isChromiumEdge // platform.js が Chromium Edge と Chrome を判別できないので手動で Edge 判定をする
    );
  },
  evaluateChrome({ version }: Pick<BrowserPlatform, 'version'>) {
    return parseFloat(version!) >= MINIMUM_SUPPPORTED_VERSION_OF_CHROME;
  },
  evaluateSafari({ version, osFamily, osVersion }: Pick<BrowserPlatform, 'version' | 'osFamily' | 'osVersion'>) {
    // Caution: iPadOS has a setting to toggle its UA between 'OS X' (to view desktop sites) and 'iOS' but the "version" is ALWAYS the iOS version.

    switch (osFamily) {
      case 'OS X':
        return parseFloat(version!) >= MINIMUM_SUPPPORTED_VERSION_OF_OS_X_SAFARI;
      case 'iOS':
        if (osVersion == null) return false;

        // Preface: iOS Safari version is the same as the OS version
        // iOS support is determined by osVersion due to Safari WebView not having precise version numbers such as '8+'
        return parseFloat(osVersion) >= MINIMUM_SUPPPORTED_VERSION_OF_IOS_SAFARI;
      default:
        return false;
    }
  },
  evaluateFirefox({ version }: Pick<BrowserPlatform, 'version'>) {
    return parseFloat(version!) >= MINIMUM_SUPPPORTED_VERSION_OF_FIREFOX;
  },
  evaluateEdge({ version }: Pick<BrowserPlatform, 'version'>) {
    return parseFloat(version!) >= MINIMUM_SUPPPORTED_VERSION_OF_EDGE;
  },
  evaluateAndroidBrowser({ userAgent }: Pick<BrowserPlatform, 'userAgent'>) {
    // RegExpMatchArray には以下の文字列が入る
    // 0: "Chrome/66"
    // 1: "66"
    const chromeMajorVersionMatchArray = userAgent.match(CHROME_MAJOR_VERSION_REGEXP_FOR_WEBVIEW) as RegExpMatchArray;
    if (chromeMajorVersionMatchArray == null) return false;

    return parseFloat(chromeMajorVersionMatchArray[1]) >= MINIMUM_SUPPPORTED_VERSION_OF_CHROME;
  },
};
